import type {
  IDailyMetricsDay,
  IUsageDailyMetricsResponse,
} from '@/apiClient/types/metrics'

/**
 * The server recomputes today in the background on every request and notifies
 * when done, so a ready day is refetched at most this often.
 */
const READY_DAY_REFRESH_AFTER_MS = 5 * 60 * 1000

/** Responses from before `status` existed are all materialized. */
export const isUsageDailyDayReady = (day: IDailyMetricsDay) =>
  (day.status ?? 'ready') === 'ready'

/** Replaces the days with matching dates and rebuilds `computing_days`. */
export function withUsageDailyDays(
  data: IUsageDailyMetricsResponse,
  replacementDays: IDailyMetricsDay[],
): IUsageDailyMetricsResponse {
  const replacementByDate = new Map(
    replacementDays.map((day) => [day.bucket_date, day]),
  )
  const days = data.days.map(
    (day) => replacementByDate.get(day.bucket_date) ?? day,
  )
  return {
    ...data,
    days,
    computing_days: days
      .filter((day) => day.status === 'computing')
      .map((day) => day.bucket_date),
  }
}

/**
 * Whether a `daily_metrics_computed` for this date should refetch. A ready day
 * refetches when this browser last refetched it over 5 minutes ago (or never);
 * the browser's own clock is used, as it can differ from the server's.
 */
export function isUsageDailyDateOutdated(
  data: IUsageDailyMetricsResponse,
  bucketDate: string,
  lastRefetchedAtMs: number | undefined,
  nowMs = Date.now(),
): boolean {
  const day = data.days.find(
    (candidate) => candidate.bucket_date === bucketDate,
  )
  if (!day) return false
  if (!isUsageDailyDayReady(day)) return true
  return (
    lastRefetchedAtMs === undefined ||
    nowMs - lastRefetchedAtMs > READY_DAY_REFRESH_AFTER_MS
  )
}

/** Marks a computing day failed. A ready day keeps its data: only its refresh failed. */
export function withUsageDailyDayFailed(
  data: IUsageDailyMetricsResponse,
  bucketDate: string,
): IUsageDailyMetricsResponse {
  const day = data.days.find(
    (candidate) => candidate.bucket_date === bucketDate,
  )
  if (day?.status !== 'computing') return data
  return withUsageDailyDays(data, [{ ...day, status: 'failed' }])
}

/** A refetch reports a failed day as computing again; keep it failed until it loads or is retried. */
export function withFailedUsageDailyDaysKept(
  fresh: IUsageDailyMetricsResponse,
  previous: IUsageDailyMetricsResponse | undefined,
): IUsageDailyMetricsResponse {
  const failedDates = new Set(
    previous?.days
      .filter((day) => day.status === 'failed')
      .map((day) => day.bucket_date),
  )
  if (!failedDates.size) return fresh
  return withUsageDailyDays(
    fresh,
    fresh.days
      .filter(
        (day) => day.status === 'computing' && failedDates.has(day.bucket_date),
      )
      .map((day) => ({ ...day, status: 'failed' })),
  )
}
